/**
 * 🎡 Guild Lever — daily random-item pull panel
 *
 * Drops into GuildPage.jsx (sibling of the wealth panel). Shows:
 *   • Pulls remaining today / cap
 *   • Personal Wealth balance
 *   • Big "Pull the Lever" button (20 Wealth)
 *   • Reward modal on success with revealed item
 *   • Active Protection Bubble timer when present
 *
 * No wheel animation yet — Phase 1 is "instant reveal." Phase 2 will
 * add the wheel UI.
 */
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COLOR = {
  common:   "border-neutral-700 bg-neutral-900/40 text-neutral-200",
  uncommon: "border-emerald-700/60 bg-emerald-900/20 text-emerald-200",
  rare:     "border-amber-500 bg-amber-500/15 text-amber-200 shadow-[0_0_24px_-8px_rgba(245,158,11,0.6)]",
};


function fmtCountdown(secs) {
  if (!secs || secs <= 0) return null;
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h >= 1) return `${h}h ${m}m`;
  return `${m}m`;
}


export default function GuildLeverPanel({ onWealthChanged }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [reveal, setReveal] = useState(null); // last single-pull reward shown in modal
  const [bulkResult, setBulkResult] = useState(null); // bulk-pull result for the modal

  const load = async () => {
    try {
      const r = await axios.get(`${API}/guild/lever/state`);
      setState(r.data);
    } catch {
      /* silent — panel just stays empty */
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onPull = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/guild/lever/pull`);
      setState(r.data.state);
      setReveal(r.data.reward);
      onWealthChanged?.();
      toast.success(`🎡 ${r.data.reward.label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The lever wouldn't budge.");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const onPullAll = async () => {
    if (busyRef.current) return;
    if (!window.confirm(
      `🎰 VIP: pull EVERY remaining spin for today?\n\n` +
      `That's up to ${state?.pulls_remaining_today ?? state?.daily_cap ?? 30} pulls at 20 💎 each.\n` +
      `Need enough Wealth to cover it — capped at what you can afford.`
    )) return;
    busyRef.current = true;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/guild/lever/pull-all`);
      setState(r.data.state);
      setBulkResult(r.data);
      onWealthChanged?.();
      toast.success(`🎰 ${r.data.pulls} pulls · ${r.data.summary.wealth_spent} 💎 spent`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk lever wouldn't budge.");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  if (!state || !state.in_guild) return null;

  const canAfford = state.wealth >= state.cost_per_pull;
  const hasPulls = state.pulls_remaining_today > 0;
  const canPull = canAfford && hasPulls && !busy;
  const isVip = !!state.is_premium;
  const inv = state.bubble_inventory || {};
  const totalBubbles = (inv.bubble_4h_count || 0) + (inv.bubble_8h_count || 0);

  return (
    <>
      <div
        data-testid="guild-lever-panel"
        className="rounded-xl border-2 border-fuchsia-700/50 bg-gradient-to-br from-purple-950/30 via-black/60 to-fuchsia-900/15 p-4 sm:p-6"
      >
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-300/80">Guild Hall · Amenity</div>
            <h3 className="text-xl sm:text-2xl font-black text-amber-100" style={{ fontFamily: "Anton, sans-serif" }}>
              🎡 THE GUILD LEVER
            </h3>
            <p className="text-[11px] text-amber-100/80 italic font-serif mt-1">
              20 💎 Wealth · random spoils · resets at 00:00 PT
            </p>
          </div>

          {totalBubbles > 0 && (
            <div className="rounded-full bg-sky-500/15 border border-sky-400/40 px-3 py-1.5 text-[11px] text-sky-200 font-bold flex items-center gap-1.5"
                 data-testid="guild-lever-bubble-inventory"
                 title="Bubbles are consumables in your bag — activate from your inventory to protect yourself + Chits + Grainhouse.">
              🫧 {inv.bubble_4h_count > 0 && <span>{inv.bubble_4h_count}×4h</span>}
              {inv.bubble_4h_count > 0 && inv.bubble_8h_count > 0 && <span> · </span>}
              {inv.bubble_8h_count > 0 && <span>{inv.bubble_8h_count}×8h</span>}
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="rounded-lg bg-black/40 border border-neutral-800 p-2 text-center">
            <div className="text-[9px] uppercase tracking-widest text-neutral-500">Wealth</div>
            <div className="text-lg font-black text-purple-200 tabular-nums" data-testid="lever-wealth">
              💎 {state.wealth.toLocaleString()}
            </div>
          </div>
          <div className="rounded-lg bg-black/40 border border-neutral-800 p-2 text-center">
            <div className="text-[9px] uppercase tracking-widest text-neutral-500">Pulls Left</div>
            <div className="text-lg font-black text-amber-200 tabular-nums" data-testid="lever-pulls-left">
              {state.pulls_remaining_today} / {state.daily_cap}
            </div>
          </div>
          <div className="rounded-lg bg-black/40 border border-neutral-800 p-2 text-center">
            <div className="text-[9px] uppercase tracking-widest text-neutral-500">Cost</div>
            <div className="text-lg font-black text-fuchsia-300 tabular-nums">
              20 💎
            </div>
          </div>
        </div>

        <button
          onClick={onPull}
          disabled={!canPull}
          data-testid="guild-lever-pull-btn"
          className={`w-full px-5 py-3 rounded-lg font-black text-sm uppercase tracking-widest transition ${
            canPull
              ? "bg-gradient-to-r from-fuchsia-500 to-amber-500 hover:from-fuchsia-400 hover:to-amber-400 text-black"
              : "bg-neutral-800 text-neutral-500 cursor-not-allowed"
          }`}
        >
          {busy ? "🎡 Rolling..." : canPull ? "🎡 PULL THE LEVER" : !hasPulls ? "🛌 Resets at 00:00 PT" : "💎 Need 20 Wealth"}
        </button>

        {/* 🎰 VIP-only bulk pull */}
        {isVip ? (
          <button
            onClick={onPullAll}
            disabled={!canPull}
            data-testid="guild-lever-pull-all-btn"
            className={`w-full mt-2 px-5 py-2.5 rounded-lg font-black text-xs uppercase tracking-widest border-2 transition flex items-center justify-center gap-2 ${
              canPull
                ? "bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 hover:from-amber-400 hover:to-amber-400 text-black border-amber-300 shadow-[0_0_24px_-8px_rgba(245,158,11,0.7)]"
                : "bg-neutral-900 text-neutral-600 border-neutral-700 cursor-not-allowed"
            }`}
            title="Pulls every remaining spin today in one shot. VIP perk."
          >
            <span>👑</span>
            <span>VIP · PULL ALL {state.pulls_remaining_today > 0 ? `(${state.pulls_remaining_today}x)` : ""}</span>
            <span>👑</span>
          </button>
        ) : (
          <div className="mt-2 text-[10px] text-center text-amber-300/60 italic">
            👑 Upgrade to VIP to unlock <b>Pull All At Once</b> bulk lever
          </div>
        )}

        {/* Reward pool preview — collapsed by default */}
        <details className="mt-3" data-testid="lever-pool-details">
          <summary className="text-[11px] text-neutral-400 cursor-pointer hover:text-amber-200">
            What&apos;s in the lever? <span className="text-neutral-600">▾</span>
          </summary>
          <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[11px]">
            {state.pool_preview.map((p) => (
              <li
                key={p.id}
                className={`rounded border px-2 py-1.5 flex items-center justify-between ${TIER_COLOR[p.tier] || TIER_COLOR.common}`}
              >
                <span>{p.label}</span>
                <span className="text-[10px] opacity-70">{p.weight}%</span>
              </li>
            ))}
          </ul>
        </details>
      </div>

      {/* Single-pull reward reveal modal */}
      {reveal && (
        <RewardModal reward={reveal} onClose={() => setReveal(null)} />
      )}
      {/* Bulk-pull list modal */}
      {bulkResult && (
        <BulkRewardModal result={bulkResult} onClose={() => setBulkResult(null)} />
      )}
    </>
  );
}


function RewardModal({ reward, onClose }) {
  // 🧪 Feb 2026 — Battle Bag drop reveals the specific potion that rolled.
  const isBagDrop = reward.kind === "bag_consumable_random" && reward.consumable;
  const displayIcon  = isBagDrop ? reward.consumable.icon : reward.label.split(" ")[0];
  const displayLabel = isBagDrop
    ? `${reward.consumable.icon} ${reward.consumable.name} ×${reward.consumable.count}`
    : reward.label;
  const displayTier  = isBagDrop ? reward.consumable.tier : reward.tier;
  const displaySub   = isBagDrop
    ? reward.consumable.description
    : `${reward.tier} · ${reward.kind.replace(/_/g, " ")}`;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
      data-testid="lever-reward-modal"
      onClick={onClose}
    >
      <div
        className={`relative w-full max-w-sm rounded-2xl border-2 p-6 text-center ${TIER_COLOR[displayTier] || TIER_COLOR.common}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[10px] uppercase tracking-[0.4em] mb-2 opacity-80">You pulled the lever!</div>
        <div className="text-5xl mb-3">{displayIcon}</div>
        <div className="text-xl font-black mb-1" style={{ fontFamily: "Anton, sans-serif" }}>
          {displayLabel}
        </div>
        <div className="text-[10px] uppercase tracking-widest opacity-70 mb-4">
          {displaySub}
        </div>
        <button
          onClick={onClose}
          data-testid="lever-reward-modal-close"
          className="w-full px-4 py-2 rounded bg-black/60 hover:bg-black/80 text-amber-200 font-black text-sm uppercase tracking-widest border border-amber-500/40"
        >
          Claim →
        </button>
      </div>
    </div>
  );
}


function BulkRewardModal({ result, onClose }) {
  const { pulls, rewards, summary } = result;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
      data-testid="lever-bulk-modal"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-2xl border-2 border-amber-400 bg-gradient-to-br from-amber-950/40 via-black to-amber-900/30 p-5 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center mb-4">
          <div className="text-[10px] uppercase tracking-[0.4em] text-amber-300/90 mb-1">👑 VIP Bulk Pull</div>
          <div className="text-4xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
            {pulls}× SPOILS
          </div>
          <div className="text-[11px] text-neutral-400 mt-1">
            spent <b className="text-fuchsia-300">{summary.wealth_spent} 💎</b> Wealth
          </div>
        </div>

        {/* Summary chips */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          <div className="rounded-lg bg-black/60 border border-amber-700/40 p-2 text-center">
            <div className="text-[9px] uppercase tracking-widest text-amber-300/70">Embersweet</div>
            <div className="text-base font-black text-amber-200">🍓 ×{summary.embersweet_total}</div>
          </div>
          <div className="rounded-lg bg-black/60 border border-amber-700/40 p-2 text-center">
            <div className="text-[9px] uppercase tracking-widest text-amber-300/70">Phoenix Tokens</div>
            <div className="text-base font-black text-amber-200">🐦‍🔥 {summary.phoenix_tokens_total}</div>
          </div>
          <div className="rounded-lg bg-black/60 border border-sky-700/40 p-2 text-center">
            <div className="text-[9px] uppercase tracking-widest text-sky-300/70">4h 🫧</div>
            <div className="text-base font-black text-sky-200">×{summary.bubble_4h_total}</div>
          </div>
          <div className="rounded-lg bg-black/60 border border-sky-700/40 p-2 text-center">
            <div className="text-[9px] uppercase tracking-widest text-sky-300/70">8h 🫧</div>
            <div className="text-base font-black text-sky-200">×{summary.bubble_8h_total}</div>
          </div>
        </div>

        {/* Full ordered list */}
        <div className="rounded-lg border border-neutral-800 bg-black/40 max-h-72 overflow-y-auto" data-testid="lever-bulk-list">
          {rewards.map((r, i) => (
            <div
              key={i}
              className={`flex items-center justify-between px-3 py-1.5 text-[12px] border-b border-neutral-900 last:border-b-0 ${TIER_COLOR[r.tier] || TIER_COLOR.common}`}
            >
              <span className="text-neutral-500 tabular-nums w-6 text-right">#{i + 1}</span>
              <span className="flex-1 truncate ml-2">{r.label}</span>
              <span className="text-[9px] uppercase tracking-widest opacity-70">{r.tier}</span>
            </div>
          ))}
        </div>

        <button
          onClick={onClose}
          data-testid="lever-bulk-modal-close"
          className="mt-4 w-full px-4 py-2 rounded bg-amber-500 hover:bg-amber-400 text-black font-black text-sm uppercase tracking-widest"
        >
          Claim All →
        </button>
      </div>
    </div>
  );
}

